import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { storage } from '../lib/utils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = storage.get('user');
      
      if (token && savedUser) {
        setUser(savedUser);
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          storage.set('user', response.data);
        } catch (err) {
          console.error('Auth check failed:', err);
          logout();
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user: userData, store_slug } = response.data;
      
      localStorage.setItem('token', token);
      storage.set('user', userData);
      setUser(userData);
      
      // Return full response so login page can use store_slug
      return { user: userData, store_slug };
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore durante il login';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await authAPI.register(data);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      storage.set('user', userData);
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore durante la registrazione';
      setError(message);
      throw new Error(message);
    }
  };

  const setUserFromGoogle = (userData) => {
    // Called from AuthCallback after Google OAuth
    setUser(userData);
    storage.set('user', userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('storeSlug');
    localStorage.removeItem('platform_admin_impersonating');
    localStorage.removeItem('platform_admin_store_slug');
    storage.remove('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    storage.set('user', userData);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      storage.set('user', response.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      register,
      logout,
      updateUser,
      refreshUser,
      setUserFromGoogle,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
